/**
 * MHDF Momentum — Subscription Context
 * 
 * Manages subscription state across the React app.
 * Provides hooks for checking access, plan info, and trial status.
 * 
 * Setup:
 *   1. Place this file at: client/src/context/SubscriptionContext.js
 *   2. Wrap your app with <SubscriptionProvider> in App.js (inside AuthProvider)
 *   3. Use the useSubscription() hook in any component
 * 
 * In App.js:
 *   import { SubscriptionProvider } from './context/SubscriptionContext';
 *   
 *   <AuthProvider>
 *     <SubscriptionProvider>
 *       <Router>...</Router>
 *     </SubscriptionProvider>
 *   </AuthProvider>
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SubscriptionContext = createContext(null);

// API base URL from environment
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get auth token from localStorage (matching your existing AuthContext pattern)
  const getToken = () => localStorage.getItem('token');

  const fetchSubscription = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setSubscription({ plan: 'none', status: 'none' });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/stripe/subscription-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
        setError(null);
      } else if (response.status === 401) {
        // Token expired or invalid — user needs to re-login
        setSubscription({ plan: 'none', status: 'none' });
      } else {
        setSubscription({ plan: 'none', status: 'none' });
        setError('Failed to load subscription');
      }
    } catch (err) {
      console.error('Subscription fetch error:', err);
      setSubscription({ plan: 'none', status: 'none' });
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and when token changes
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Listen for auth changes (login/logout) via storage events
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        fetchSubscription();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchSubscription]);

  // ── Computed values ──
  const isLegacyPlan = typeof subscription?.plan === 'string' && subscription.plan.startsWith('legacy_');
  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing' || isLegacyPlan;
  const isTrial = subscription?.status === 'trialing';
  const isPastDue = subscription?.status === 'past_due';
  const isCanceled = subscription?.status === 'canceled';
  const isAdmin = subscription?.isAdmin === true;
  const hasSubscription = subscription?.plan !== 'none' && subscription?.plan !== undefined;

  // Plan type checks
  const isClientPlan = ['foundation', 'guided', 'recovery', 'voucher', 'legacy_client'].includes(subscription?.plan);
  const isClinicianPlan = ['clinician_practitioner', 'clinician_practice', 'clinician_org', 'legacy_clinician', 'legacy_clergy'].includes(subscription?.plan);
  const isInstitutional = subscription?.plan === 'institutional';

  // Trial days remaining
  const trialDaysRemaining = isTrial && subscription?.trialEnd
    ? Math.max(0, Math.ceil((new Date(subscription.trialEnd) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  // Subscription days remaining until renewal/expiry
  const daysUntilRenewal = subscription?.currentPeriodEnd
    ? Math.max(0, Math.ceil((new Date(subscription.currentPeriodEnd) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  // ── Checkout helper ──
  const startCheckout = async (planKey, billingCycle, couponCode = null) => {
    const token = getToken();
    if (!token) {
      window.location.href = '/login?redirect=pricing';
      return;
    }

    try {
      const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planKey, billingCycle, couponCode }),
      });

      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url; // Redirect to Stripe Checkout
      } else {
        throw new Error(data.error || 'Failed to start checkout');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      throw err;
    }
  };

  // ── Customer Portal helper ──
  const openPortal = async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/stripe/customer-portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url; // Redirect to Stripe Portal
      } else {
        throw new Error(data.error || 'Failed to open billing portal');
      }
    } catch (err) {
      console.error('Portal error:', err);
      throw err;
    }
  };

  const value = {
    // State
    subscription,
    loading,
    error,

    // Status checks
    isActive,
    isTrial,
    isPastDue,
    isCanceled,
    isAdmin,
    isLegacyPlan,
    hasSubscription,
    isClientPlan,
    isClinicianPlan,
    isInstitutional,

    // Computed
    trialDaysRemaining,
    daysUntilRenewal,
    plan: subscription?.plan || 'none',
    status: subscription?.status || 'none',
    clientSlots: subscription?.clientSlots || 0,
    cancelAtPeriodEnd: subscription?.cancelAtPeriodEnd || false,

    // Actions
    startCheckout,
    openPortal,
    refresh: fetchSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

export default SubscriptionContext;
